/**
 * Captures du shell mobile (C11, spec § 4) : envoi immédiat quand le réseau est là,
 * mise en file IndexedDB sinon, rejeu à l'ouverture et sur `online`
 * (`useCaptureQueueSync`). Le store `notes` reçoit la note AU SUCCÈS seulement.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getTokenRedirect } from '../../utils/authRedirect';
import { tokenRequest } from '../../authConfig';
import { noteService, type CaptureEntree, type CapturePassage } from '../../services/noteAPI';
import type { Note } from '../../types/api';
import { NOTIFICATION_SEVERITY } from '../../constants';
import { ENTREE_TABLE_LABELS } from '../../components/entrees/entreeTableLabels';
import { setStatusBar } from '../modals/modalSlice';
import {
  createIndexedDbStore, isFailedCapture, isNetworkError, newQueuedCapture,
  type CaptureQueueStore, type QueuedCapture,
} from './captureQueue';

export type CaptureOutcome = { queued: true; item: QueuedCapture } | { queued: false; note: Note };

interface CaptureQueueState { items: QueuedCapture[]; loaded: boolean; replaying: boolean }

const initialState: CaptureQueueState = { items: [], loaded: false, replaying: false };

let customStore: CaptureQueueStore | null = null;
let defaultStore: CaptureQueueStore | null = null;
const store = (): CaptureQueueStore => customStore ?? (defaultStore ??= createIndexedDbStore());
/** Tests : injecter un magasin en mémoire ; `null` revient à IndexedDB. */
export function setCaptureQueueStore(next: CaptureQueueStore | null): void { customStore = next; }

const errorMessage = (error: unknown) => (error instanceof Error && error.message) || 'erreur inconnue';

export interface CaptureFailure { status?: number; message?: string }
export interface CaptureMobileArgs { text: string; passage?: CapturePassage; provenanceLabel?: string; entrees?: CaptureEntree[]; noteType?: 'commentary'; instance: any }

const toFailure = (error: unknown): CaptureFailure => axios.isAxiosError(error)
  ? { status: error.response?.status, message: (error.response?.data as { message?: string } | undefined)?.message }
  : { message: errorMessage(error) };

/** Messages du serveur qui refusent une ANCRE (C11 SP6) — la capture elle-même est saine. */
const ANCHOR_REFUSALS = new Set(["Un mot-clé n'existe pas", 'Passage introuvable dans la capture', 'Mots-clés invalides']);
const isAnchorRefusal = (error: unknown): boolean => {
  const failure = toFailure(error);
  return failure.status === 400 && !!failure.message && ANCHOR_REFUSALS.has(failure.message);
};

/**
 * Envoi d'une capture. Avec des ancres, un 400 qui refuse une ancre (mot-clé
 * disparu, texte introuvable) renvoie la capture SANS ancres plutôt que de la
 * perdre — `anchorsDropped` le dit à l'appelant (toast).
 */
async function send(text: string, instance: any, passage?: CapturePassage, entrees?: CaptureEntree[], noteType?: 'commentary'): Promise<{ note: Note; anchorsDropped: boolean }> {
  const accessToken = await getTokenRedirect(tokenRequest, instance);
  if (!accessToken) throw new Error('Impossible d\'obtenir le jeton d\'accès');
  const anchors = entrees && entrees.length > 0 ? entrees : undefined;
  // Les arguments optionnels ne sont passés que s'ils existent : un `undefined`
  // en queue d'appel n'est pas neutre pour qui observe la signature.
  const call = (withAnchors: boolean): Promise<Note> => {
    const anchorArg = withAnchors ? anchors : undefined;
    if (noteType) return noteService.capture(text, undefined, accessToken, undefined, passage, anchorArg, noteType);
    if (anchorArg) return noteService.capture(text, undefined, accessToken, undefined, passage, anchorArg);
    if (passage) return noteService.capture(text, undefined, accessToken, undefined, passage);
    return noteService.capture(text, undefined, accessToken);
  };
  if (!anchors) return { note: await call(false), anchorsDropped: false };
  try {
    return { note: await call(true), anchorsDropped: false };
  } catch (error) {
    if (!isAnchorRefusal(error)) throw error;
    return { note: await call(false), anchorsDropped: true };
  }
}

export const loadCaptureQueue = createAsyncThunk('captureQueue/load', async () => store().list());

export const captureMobileAsync = createAsyncThunk<CaptureOutcome, CaptureMobileArgs, { rejectValue: CaptureFailure }>(
  'captureQueue/capture',
  async ({ text, passage, provenanceLabel, entrees, noteType, instance }, { dispatch, rejectWithValue }) => {
    const enqueue = async (): Promise<CaptureOutcome> => {
      const item = newQueuedCapture(text, new Date(), { passage, provenanceLabel, entrees, noteType });
      await store().put(item);
      dispatch(setStatusBar({ open: true, message: 'Enregistrée, sera envoyée au retour du réseau', severity: NOTIFICATION_SEVERITY.INFO }));
      return { queued: true, item };
    };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return enqueue();
    try {
      const { note, anchorsDropped } = await send(text, instance, passage, entrees, noteType);
      dispatch(setStatusBar(anchorsDropped
        ? { open: true, message: passage ? ENTREE_TABLE_LABELS.notes.citationSansConcept : ENTREE_TABLE_LABELS.notes.captureSansConcept, severity: NOTIFICATION_SEVERITY.WARNING, action: { label: 'Voir', kind: 'navigate', to: passage ? `/notes/${note._id}` : '/notes' } }
        : passage
          ? { open: true, message: noteType === 'commentary' ? 'Commentaire enregistré' : 'Citation enregistrée', severity: NOTIFICATION_SEVERITY.SUCCESS, action: { label: 'Voir', kind: 'navigate', to: `/notes/${note._id}` } }
          : { open: true, message: 'Dans votre boîte d\'entrée', severity: NOTIFICATION_SEVERITY.SUCCESS, action: { label: 'Voir', kind: 'navigate', to: '/notes' } }));
      return { queued: false, note };
    } catch (error) {
      if (isNetworkError(error)) return enqueue();
      return rejectWithValue(toFailure(error)); // 4xx/5xx : la sheet garde le texte et affiche l'échec
    }
  },
);

export const replayCaptureQueue = createAsyncThunk<{ sent: Note[]; remaining: QueuedCapture[] }, { instance: any }>(
  'captureQueue/replay',
  async ({ instance }, { dispatch }) => {
    const sent: Note[] = [];
    let sentWithPassage = 0;
    const pending = await store().list();
    let stopped = false;
    for (const item of pending) {
      if (stopped || isFailedCapture(item)) continue;
      try {
        sent.push((await send(item.text, instance, item.passage, item.entrees, item.noteType)).note);
        if (item.passage) sentWithPassage += 1;
        await store().remove(item.id);
      } catch (error) {
        if (isNetworkError(error)) { stopped = true; continue; }
        await store().put({ ...item, attempts: item.attempts + 1, lastError: errorMessage(error) });
      }
    }
    if (sent.length > 0) {
      dispatch(setStatusBar({
        open: true, severity: NOTIFICATION_SEVERITY.SUCCESS,
        message: sentWithPassage > 0
          ? (sent.length === 1 ? 'Citation envoyée' : `${sent.length} captures envoyées`)
          : (sent.length === 1 ? 'Capture envoyée — dans votre boîte d\'entrée' : `${sent.length} captures envoyées — dans votre boîte d'entrée`),
      }));
    }
    return { sent, remaining: await store().list() };
  },
  { condition: (_, { getState }) => !(getState() as { captureQueue: CaptureQueueState }).captureQueue.replaying },
);

export const retryQueuedCapture = createAsyncThunk<void, { id: string; instance: any }>(
  'captureQueue/retry',
  async ({ id, instance }, { dispatch }) => {
    const item = (await store().list()).find(i => i.id === id);
    if (!item) return;
    await store().put({ ...item, attempts: 0, lastError: undefined });
    await dispatch(replayCaptureQueue({ instance }));
  },
);

export const discardQueuedCapture = createAsyncThunk<string, string>('captureQueue/discard', async (id) => {
  await store().remove(id);
  return id;
});

const captureQueueSlice = createSlice({
  name: 'captureQueue',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadCaptureQueue.fulfilled, (state, action) => { state.items = action.payload; state.loaded = true; })
      .addCase(captureMobileAsync.fulfilled, (state, action) => {
        if (action.payload.queued) state.items = [...state.items, action.payload.item].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      })
      .addCase(replayCaptureQueue.pending, (state) => { state.replaying = true; })
      .addCase(replayCaptureQueue.fulfilled, (state, action) => { state.items = action.payload.remaining; state.replaying = false; })
      .addCase(replayCaptureQueue.rejected, (state) => { state.replaying = false; })
      .addCase(discardQueuedCapture.fulfilled, (state, action) => { state.items = state.items.filter(i => i.id !== action.payload); });
  },
});

export const selectQueuedCaptures = (state: any): QueuedCapture[] => state.captureQueue?.items ?? [];
export const selectCaptureQueueLoaded = (state: any): boolean => state.captureQueue?.loaded ?? false;

export default captureQueueSlice.reducer;
